<template>
  <Loader v-if="isDatasetLoading || isLoading" class="mr-2 mt-6" />
  <v-stepper v-else v-model="step">
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1">
        {{ `${$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update")}` }}
      </v-stepper-step>

      <template v-if="!['dictionary','ts'].includes(datasetType)">
        <v-divider></v-divider>
        <v-stepper-step step="2">
          {{ `${$vuetify.lang.t("$vuetify.training.train")}` }} 
        </v-stepper-step>
      </template>
    </v-stepper-header>

    <v-stepper-items>
      <v-stepper-content step="1">
        <UploadDataset
        @mandatory-status="isCheckingMandatoryColumns = $event" ref="dataset"
        :is-url-reachable.sync="isUrlReachable"
        :is-reachabilty-checking="isReachabiltyChecking"
        @open-cloud-picker="openCloudPickerForSelection"
        />
        <CloudFilePicker
          v-if="cloudPickerOpen"
          v-model="cloudPickerOpen"
          :project-id="$route.query.pid"
          :provider="selectedGptDataset.cloudProvider"
          :allowed-extensions="cloudFileExtensions"
          :existing-items="existingCloudItems"
          @select="handleCloudFileSelect"
          @cancel="handleCloudPickerCancel"
        />
        <div class="d-flex justify-space-between">
          <div>
            <v-btn
            :style="{ visibility: !datasetUpdateBtnLoading ? 'visible' : 'hidden' }"
              outlined
              color="error"
              class="mr-4"
              :loading="false"
              @click="cancelBtnClick"
            >
              {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
            </v-btn>
          </div>
          <div>
            <v-btn
              outlined
              color="primary"
              v-if="gptSetupState.dataset.completed && !datasetUpdateBtnLoading && !isTimeSeries"
              class="mr-4"
              @click="skip(step)"
            >
              {{ $vuetify.lang.t("$vuetify.common.skip_update") }}
            </v-btn>
            <v-btn
              color="primary"
              class="mr-4"
              :loading="datasetUpdateBtnLoading"
              @click="nextStep(step)"
              :disabled="isCheckingMandatoryColumns || !$hasProjectAccessFor([$pRole.ADMIN])"
            >
              {{ $vuetify.lang.t("$vuetify.common.continue_btn") }}
            </v-btn>
          </div>
        </div>
      </v-stepper-content>

      <v-stepper-content step="2">
        <div v-if="datasetFileUploadInProgress">
          <div
            style="min-height: 200px"
            class="d-flex justify-center align-center"
          >
            <v-col cols="6">
              <v-list-item>
                <v-list-item-icon>
                  <v-progress-circular indeterminate color="primary">
                  </v-progress-circular>
                </v-list-item-icon>

                <v-list-item-content>
                  {{ this.isTimeSeries ? $vuetify.lang.t("$vuetify.gpt_dataset.dataset_ts_process_progress")
                    : isDictionaryDataset ?  $vuetify.lang.t('$vuetify.gpt_dataset.dataset_create_processing_message')
                    : $vuetify.lang.t("$vuetify.gpt_dataset.dataset_process_progress")
                  }}
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </div>
        </div>
        <div
          v-else-if="gptSetupState.dataset.error"
          style="min-height: 200px"
          class="d-flex justify-center align-center flex-column"
        >
          <div class="d-flex">
            <v-icon :style="{ color: 'red' }">mdi-close-circle-outline</v-icon>
            <v-list-item-title style="margin-left: 10px">
              {{ gptSetupState.dataset.message }}
            </v-list-item-title>
          </div>
          <v-btn
            style="margin-top: 10px"
            color="primary"
            class="mr-4"
            outlined
            :loading="false"
            @click="datasetErrorTryAgain"
          >
            {{ $vuetify.lang.t("$vuetify.common.upload_again") }}
          </v-btn>
        </div>
        <!-- training error -->
        <div
          v-else-if="gptSetupState.training.error && !isDictionaryDataset"
          style="min-height: 200px"
          class="d-flex justify-center align-center flex-column"
        >
          <div class="d-flex">
            <v-icon :style="{ color: 'red' }">mdi-close-circle-outline</v-icon>
            <v-list-item-title style="margin-left: 10px">
              {{ gptSetupState.training.message }}
            </v-list-item-title>
          </div>
          <v-btn
            style="margin-top: 10px"
            color="primary"
            class="mr-4"
            outlined
            :loading="false"
            @click="trainingErrorTryAgain"
          >
            {{ $vuetify.lang.t("$vuetify.common.try_again") }}
          </v-btn>
        </div>

        <div v-else-if="dataSetProcessCompleted">
          <div
            v-if="trainingInProgress && !isDictionaryDataset"
            style="min-height: 200px"
            class="d-flex justify-center align-center"
          >
            <v-col cols="6">
              <v-list-item>
                <v-list-item-icon>
                  <v-progress-circular indeterminate color="primary">
                  </v-progress-circular>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ $vuetify.lang.t("$vuetify.gpt_dataset.training_in_progress") }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </div>
          <v-form ref="train_form" v-else>
            <div
              v-if="!isDictionaryDataset"
              style="min-height: 200px"
              class="d-flex justify-center align-center"
            >
              <v-col cols="12" md="6">
                <v-select
                  :label="$vuetify.lang.t('$vuetify.gpt_dataset.training_type')"
                  :items="
                          [
                           {
                            text: this.$vuetify.lang.t('$vuetify.gpt_dataset.text_training_type'),
                            value: 'text',
                           },
                           {
                            text: this.$vuetify.lang.t('$vuetify.gpt_dataset.image_training_type'),
                            value: 'image',
                           },
                          ]"
                  v-model="trainType"
                  outlined
                  :rules="[
                    required(
                      $vuetify.lang.t('$vuetify.gpt_dataset.training_type'),
                    ),
                  ]"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="batchSize"
                  :label="$vuetify.lang.t('$vuetify.gpt_dataset.batch_size')"
                  outlined
                  type="number"
                  :rules="batchSizeRules"
                  disabled
                ></v-text-field>
              </v-col>
            </div>
          </v-form>

          <div v-if="!trainingInProgress" class="d-flex justify-space-between">
            <v-btn
              v-if="!isDictionaryDataset"
              color="primary"
              class="mr-4"
              outlined
              :loading="false"
              @click="previousStep(step)"
              :style="{ visibility: !trainingBtnLoading ? 'visible' : 'hidden' }"
            >
              {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
            </v-btn>
            <div>
              <v-btn
                v-if="!isDictionaryDataset"
                color="primary"
                class="mr-4"
                outlined
                :loading="false"
                @click="cancelBtnClick"
                :style="{ visibility: !trainingBtnLoading ? 'visible' : 'hidden' }"
              >
                {{ $vuetify.lang.t("$vuetify.common.skip") }}
              </v-btn>
              <v-btn
                @click="handleTraining"
                color="primary"
                class="mr-4 pr-8 pl-8"
                :loading="trainingBtnLoading"
                :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
              >
                {{ $vuetify.lang.t("$vuetify.training.train") }}
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else>
          <div
            style="min-height: 200px"
            class="d-flex justify-center align-center"
          >
            <v-col cols="6">
              <v-list-item>
                <v-list-item-icon>
                  <v-progress-circular indeterminate color="primary">
                  </v-progress-circular>
                </v-list-item-icon>

                <v-list-item-content>
                  <!-- <v-list-item-title> -->
                  {{
                    $vuetify.lang.t(
                      "$vuetify.gpt_dataset.dataset_process_progress",
                    )
                  }}
                  <!-- </v-list-item-title> -->
                </v-list-item-content>
              </v-list-item>
            </v-col>
          </div>
          <v-btn
            color="primary"
            class="mr-4"
            outlined
            :loading="false"
            @click="previousStep(step)"
          >
            {{ $vuetify.lang.t("$vuetify.common.prev_btn") }}
          </v-btn>
        </div>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { mapState, mapActions } from "vuex";
import CloudFilePicker from "@/components/Projects/UploadDataset/CloudFilePicker";
import UploadDataset from "@/components/Projects/UploadDataset/UploadDataset";
import {
  ADD_ALERT,
  CREATE_GPT_DATASET,
  UPDATE_GPT_DATASET_API,
  UPDATE_GPT_DATASET_FILE,
  TRAIN_GPT_DATASET,
  UPDATE_GPT_SETUP_STATE,
  GET_TASK_STATUS,
  UPDATE_GPT_DATASET_STATE,
  GET_GPT_DATASET_BY_ID,
  GET_CLOUD_STORAGE_ITEMS,
  UPDATE_GPT_DATASET_TS_FILE
} from "@/store/_actiontypes";
import { GptDatasetMapper } from "@/helpers/dataMappers/gptDataset";
import getEnv from "@/config/env";
import Loader from "@/components/common/loader/Loader";
import { datasetMixin, cloudSelectionIdsEqual } from "../mixins/datasetMixin";
import { Toast } from "../helpers/toast";

export default {
  mixins: [datasetMixin],
  components: {
    UploadDataset,
    CloudFilePicker,
    Loader
  },
  data() {
    return {
      isLoading: true,
      trainingBtnLoading: false,
      datasetUpdateBtnLoading: false,
      step: 1,
      datasetFileUploadInProgress: false,
      trainingInProgress: false,
      trainType: "",
      batchSize: 1,
      dataSetProcessCompleted: false,
      intervalId: null,
      datasetUpdateLoader: false,
      batchSizeRules: [
        value => value >= 1 || "Min value is 1",
        value => value <= 10 || "Max value is 10",
      ],
      gptSetupState: {
        completed: false,
        dataset: {
          task_id: null,
          status: null,
          dataset_id: null,
          completed: false,
        },
        training: { task_id: null, status: null, completed: false },
      },
      isCheckingMandatoryColumns: false,
      isReachabiltyChecking: false,
      isUrlReachable: true,
      hasErrorInUrl:false,
      prevUrls:[],
    };
  },
  computed: {
    ...mapState({
      gptDatasets: state => state.project.gptDatasets,
      selectedGptDataset: state => state.project.selectedGptDataset,
      datasetExist: state => state.project.gptSetupState.dataset.completed,
      selectedProject: state => state.project.selectedProject,
    }),
    isTimeSeries() {
      return this.selectedGptDataset?.dataset_type === 'time_series';
    },
    isDatasetLoading() {
      return !(this.selectedGptDataset !== null && this.selectedGptDataset.datasetId !== "");
    },
    isDictionaryDataset() {
      return Array.isArray(this.selectedGptDataset?.tags) &&
            this.selectedGptDataset.tags?.includes('dictionary');
    },
  },
  watch: {
    gptSetupState: {
      handler(setupState) {
        //for dataset task
        if (
          setupState.dataset?.task_id &&
          !this.intervalId &&
          !setupState.dataset?.error
        ) {
          this.datasetFileUploadInProgress = true;
          this.startInterval(setupState.dataset?.task_id, "dataset");
        }
        //for training task
        if (
          setupState.training?.task_id &&
          !this.intervalId &&
          !setupState.training?.error
        ) {
          this.startInterval(setupState.training?.task_id, "train");
          this.trainingInProgress = true;
        }

        if (setupState.dataset?.completed) {
          this.dataSetProcessCompleted = true;
        }
        if (setupState.training?.completed) {
          this.trainingInProgress = false;
          this.$router.push({
            path: `/projects`,
            query: {
              pid: this.$route.query.pid,
              stype: this.$route.query.stype,
              ptab: 'mairaDatasets',
            }
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadProfileAndDataset();
    if (this.$route.query.skipFirstStep === "true") {
      this.step = 2;
    } else {
      this.step = 1;
    }
  },
  beforeDestroy() {
    this.stopInterval();
    this.resetGptDataset();
    this.$store.dispatch('project/SET_DATASET_TYPE', 'regular');
    this.$store.dispatch('project/SET_CONTENT_SOURCE', 'webUrl');
  },
  methods: {
    ...mapActions("project", [
      CREATE_GPT_DATASET,
      UPDATE_GPT_DATASET_API,
      UPDATE_GPT_DATASET_FILE,
      TRAIN_GPT_DATASET,
      UPDATE_GPT_SETUP_STATE,
      UPDATE_GPT_DATASET_STATE,
      GET_GPT_DATASET_BY_ID,
      GET_CLOUD_STORAGE_ITEMS,
      UPDATE_GPT_DATASET_TS_FILE
    ]),
    ...mapActions("task", [GET_TASK_STATUS]),
    stopInterval() {
      clearInterval(this.intervalId);
      this.intervalId = null;
    },
    loadProfileAndDataset() {
      this.isLoading = true;
      if (this.gptSetupState.dataset.error) {
        this.step = 2;
      }
      this.GET_GPT_DATASET_BY_ID({
        project_id: this.$route.query.pid,
        dataset_id: this.$route.query.datasetId,
      }).then(
        res => {
          this.hydrateRestoredCloudItems();
          const resDataset = res.data.response?.dataset
          this.setDatasetType(resDataset)
          
          if(this.datasetType==='url'){
            this.setContentSource(resDataset)
            const resUrls = resDataset?.urls || []
            this.prevUrls = [...resUrls]
          }

          if (resDataset?.documents_count?.total > 0) {
            this.gptSetupState = {
              ...this.gptSetupState,
              dataset: {
                ...this.gptSetupState.dataset,
                completed: true,
                dataset_id: this.$route.query.datasetId,
              },
            };
            if (this.$route.query.skipFirstStep === "true") {
              this.step = 2;
            } else {
              this.step = 1;
            }
            // this.step = 2;
          }
        },
        _err => {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t(
                "$vuetify.gpt_dataset.dataset_fetch_error",
              ),
              color: "error",
            },
            { root: true },
          );
        },
      ).finally(() => {
        this.isLoading = false;
      });
    },
    async hydrateRestoredCloudItems() {
      const sources = this.selectedGptDataset?.cloudSources || [];
      const restored = sources.filter(
        (s) => (s.items || []).some((i) => i._restored),
      );
      if (!restored.length) return;
      const hydrated = await Promise.all(
        restored.map(async (s) => {
          try {
            const res = await this.GET_CLOUD_STORAGE_ITEMS({
              project_id: this.$route.query.pid,
              provider: s.provider,
              account_id: s.account_id,
              item_ids: s.items.map((i) => i.id),
            });
            const byId = new Map(
              (res.data?.detail?.items || []).map((i) => [i.id, i]),
            );
            return {
              ...s,
              items: s.items.map((item) => {
                const meta = byId.get(item.id);
                if (!meta || meta.missing) return item;
                return {
                  id: item.id,
                  name: meta.name || item.id,
                  size: meta.size,
                  is_folder: !!meta.is_folder,
                  mime_type: meta.mime_type,
                  account_id: s.account_id,
                };
              }),
            };
          } catch (e) {
            return s;
          }
        }),
      );
      const merged = sources.map(
        (s) =>
          hydrated.find(
            (h) => h.provider === s.provider && h.account_id === s.account_id,
          ) || s,
      );
      const dataset = this.selectedGptDataset;
      if (
        dataset?.uploadSource === "cloud" &&
        cloudSelectionIdsEqual(dataset.cloudSources, merged)
      ) {
        this.UPDATE_GPT_DATASET_STATE({
          dataset: { ...dataset, cloudSources: merged },
        });
      }
    },
    resetGptDataset() {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: GptDatasetMapper.mapBackendToFrontend({
          dataset_id: "",
          description: "",
          idx_column_name: "",
          image_url_column: "",
          is_idx_fillup_if_empty: true,
          name: "",
          secondary_idx_column: "",
        }),
      });
    },
    startInterval(task_id, type) {
      this.intervalId = setInterval(() => {
        if (type === "dataset") {
          this.GET_TASK_STATUS({
            project_id: this.$route.query.pid,
            task_id: task_id,
          })
            .then(res => {
              const status = res.data.detail?.response?.status;
              if (status === "success") {
                this.$store.dispatch(
                  `alert/${ADD_ALERT}`,
                  {
                    message: this.$vuetify.lang.t(
                      "$vuetify.gpt_dataset.dataset_update_success",
                    ),
                    color: "success",
                  },
                  { root: true },
                );
                this.stopInterval();
                this.datasetFileUploadInProgress = false;
                this.dataSetProcessCompleted = true;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    completed: true,
                    dataset_id: this.gptSetupState.dataset.dataset_id,
                    task_id: null,
                    error: false,
                  },
                };
                const trainingSkipped = this.gptSetupState?.training?.skipped === true;
                if (trainingSkipped || this.isDictionaryDataset || this.isTimeSeries) {
                  this.$router.push({
                    path: `/projects`,
                    query: { pid: this.$route.query.pid, stype: this.$route.query.stype, ptab: "mairaDatasets" },
                  });
                } else {
                  this.step = 2;
                }
              } else if (status === "failed") {
                this.stopInterval();
                this.datasetFileUploadInProgress = false;
                this.datasetUpdateBtnLoading = false;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: status,
                    message: res.data.detail?.response?.message,
                    error: true,
                  },
                };
              } else if (status !== "pending" && status !== "claimable") {
                this.stopInterval();
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: status,
                    message: res.data.detail?.response?.message,
                    error: true,
                  },
                };
              } else {
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    status: status,
                    error: false,
                  },
                };
              }
            })
            .catch(err => {
              console.error("error", err);
            });
        }
        if (type === "train") {
          this.GET_TASK_STATUS({
            project_id: this.$route.query.pid,
            task_id: task_id,
          })
            .then(res => {
              if (res.data.detail.response.status === "success") {
                this.stopInterval();
                this.trainingInProgress = false;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  completed: true,
                  training: {
                    ...this.gptSetupState.training,
                    completed: true,
                    task_id: null,
                    status: "success",
                    error: false,
                  },
                };
              } else if (
                res.data.detail.response.status !== "pending" &&
                res.data?.detail?.response?.status !== "claimable"
              ) {
                this.stopInterval();
                this.trainingInProgress = false;
                this.gptSetupState = {
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    status: res.data.detail.response.status,
                    message: res.data?.detail?.response?.message,
                    error: true,
                  },
                };
                // updated training state
              } else {
                this.gptSetupState = {
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    status: res.data.detail.response.status,
                    error: false,
                  },
                };
              }
            })
            .catch(err => {
              console.error("error", err);
            });
        }
      }, getEnv("VUE_APP_POLLING_INTERVAL"));
    },
    trainingErrorTryAgain() {
      this.gptSetupState = {
        ...this.gptSetupState,
        training: {
          ...this.gptSetupState.training,
          task_id: null,
          status: null,
          message: null,
          error: false,
        },
      };
    },
    datasetErrorTryAgain() {
      // this.step = 1
      if (this.$route.query.skipFirstStep === "true") {
        this.step = 2;
      } else {
        this.step = 1;
      }
      this.gptSetupState = {
        ...this.gptSetupState,
        dataset: {
          ...this.gptSetupState.dataset,
          task_id: null,
          status: null,
          message: null,
          error: false,
        },
      };
    },

    previousStep(step) {
      if (this.isDictionaryDataset && step === 2) {
        this.step = 1;
        return;
      }
      this.step = step - 1;
      if (step === 1) this.step = 2;
    },
    required(fieldName) {
      return (value) => {
        return !!value || `${fieldName}${this.$vuetify.lang.t('$vuetify.validation.required_error_msg_txt')}`;
      };
    },
    cancelBtnClick() {
      this.$router.push({
        path: `/projects`,
        query: {
          pid: this.$route.query.pid,
          stype: this.$route.query.stype,
          ptab: 'mairaDatasets',
        }
      });
    },
    skip(step) {
      if ((this.isTimeSeries|| this.isDictionaryDataset) && step === 1) {
        setTimeout(() => {
          this.$router.push({
            path: `/projects`,
            query: {
              pid: this.$route.query.pid,
              stype: this.$route.query.stype,
              ptab: 'mairaDatasets'
            }
          });
          this.datasetUpdateLoader = false;
        }, 500);
      } else {
        this.step = step + 1;
      }
    },    
    updateTimeSeriesDataset(){
      const isCloud = this.selectedGptDataset.uploadSource === "cloud";
      return this.UPDATE_GPT_DATASET_TS_FILE({
        project_id: this.$route.query.pid,
        datasetId: this.selectedGptDataset.datasetId,
        ...(isCloud
          ? {
              cloud_sources: this.selectedGptDataset.cloudSources,
            }
          : { datasetFile: this.selectedGptDataset.datasetFile }),
      })
      .then(res => {
        if (res.data.detail?.task_id) {
          this.gptSetupState = {
            ...this.gptSetupState,
            dataset: {
              ...this.gptSetupState.dataset,
              task_id: res.data.detail?.task_id,
            },
          };
          this.step = 2

        }else{
          this.dataSetProcessCompleted = true;
        }
          const successMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_success")
          Toast.success(successMsg)
      })
      .catch(err => {
        const errMsg = this.getDatasetErrorMessage(err, isCloud)
        Toast.error(errMsg)
      })

    },

    isFormValid() {
      let isValid = true;
      const dataset = this.$refs?.dataset;
      const mainForm = dataset?.$refs?.dataset_form;

      // Validate time-series form if dataset type is TS
      if (this.datasetType === 'ts') {
        const tsComponent = dataset?.$refs?.datasetTs;
        const tsForm = tsComponent?.$refs?.dataset_form;

        if (!tsComponent || !tsForm?.validate()) {
          isValid = false;
        }
      }

      // Validate main dataset form
      if (!mainForm?.validate()) {
        isValid = false;
      }

      return isValid;
    },     
    nextStep(step) {
      if (step === 1) {
        if(!this.isFormValid()) return;

        if (
          this.selectedGptDataset.uploadSource === "cloud" &&
          !this.selectedGptDataset.cloudSources?.some(
            (s) => s.items?.length > 0,
          )
        ) {
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t(
                "$vuetify.gpt_dataset.cloud_storage.no_file_selected",
              ),
              color: "warning",
            },
            { root: true },
          );
          return;
        }

        if(this.datasetType==='url' && this.contentSource === 'webUrl'){
          this.isReachabiltyChecking = true
        }
        
        setTimeout(()=>{          
          let payload = this.buildDatasetUpdatePayload(this.selectedGptDataset);

          this.datasetUpdateLoader = true;
          this.datasetUpdateBtnLoading= true;
          this.UPDATE_GPT_DATASET_API({
            project_id: this.$route.query.pid,
            dataset: {
              ...payload,
               datasetId: this.selectedGptDataset.datasetId,
               ...(!['url','ts'].includes(this.datasetType) && {datasetFile:this.selectedGptDataset.datasetFile}),
               ...(this.selectedGptDataset.uploadSource === "cloud" &&
                 this.selectedGptDataset.cloudSourcesDirty && {
                   cloudSources: this.selectedGptDataset.cloudSources,
                 }),
              }
          })
          .then((res)=>{
            if (this.datasetType === 'ts') this.notifyTimezoneBackfill(res);
            if(this.datasetType==='ts' && (this.selectedGptDataset.datasetFile ||
                (this.selectedGptDataset.uploadSource === "cloud" &&
                  this.selectedGptDataset.cloudSourcesDirty &&
                  this.selectedGptDataset.cloudSources?.some(
                    (s) => s.items?.length > 0,
                  )))){
              return this.updateTimeSeriesDataset()
            }
            else{
              this.gptSetupState = {
                ...this.gptSetupState,
                dataset: {
                  ...this.gptSetupState.dataset,
                  task_id: res.data.detail?.task_id,
                },
              };

              if (res.data.detail?.task_id) {
                this.datasetFileUploadInProgress = true;
              }else{
                let dataSetProcessCompleted = this.datasetType !=='url' || (this.datasetType==='url' && !this.isUrlsChanged(this.prevUrls, payload.urls))
                this.gptSetupState = {
                  ...this.gptSetupState,
                  dataset: {
                    ...this.gptSetupState.dataset,
                    completed: dataSetProcessCompleted,
                    task_id: res.data.detail?.task_id,
                  },
                };
                const msg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_success")
                Toast.success(msg);
              }
              // is url change
              if(this.datasetType === 'url' && this.isUrlsChanged(this.prevUrls, payload.urls)){

                if(this.contentSource === 'webUrl') {
                  this.prevUrls = [...payload.urls]
                }
                
                this.datasetFileUploadInProgress = true;
                this.UPDATE_GPT_DATASET_FILE({
                  project_id: this.$route.query.pid,
                  datasetId: this.selectedGptDataset.datasetId,
                  ...(this.datasetType==='url' && { scrape_urls:true }),
                }).then((res)=>{
                  this.gptSetupState = {
                    ...this.gptSetupState,
                    dataset: {
                      ...this.gptSetupState.dataset,
                      task_id: res.data.detail?.task_id,
                    },
                  };
                  if(step && step===1){
                    this.step = step + 1;
                  }
                }).catch((_err)=>{
                  this.datasetFileUploadInProgress = false
                  const errMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_update_error");
                  Toast.error(errMsg);
                })
              }

              if (this.datasetType==='dictionary') {
                this.gptSetupState = {
                  ...this.gptSetupState,
                  training: {
                    ...this.gptSetupState.training,
                    skipped: true,
                    completed: false,
                    task_id: null,
                    status: "skipped",
                    error: false,
                  },
                  dataset: {
                    ...this.gptSetupState.dataset,
                    task_id: res.data.detail?.task_id || this.gptSetupState.dataset.task_id,
                    dataset_id: res.data.detail?.dataset_id || this.gptSetupState.dataset.dataset_id,
                  },
                };

                const selectedDataset = this.selectedGptDataset;
                const selectedDatasetFile = selectedDataset.datasetFile;
                const hasCloudSelection =
                  selectedDataset.uploadSource === "cloud" &&
                  selectedDataset.cloudSourcesDirty &&
                  selectedDataset.cloudSources?.some(
                    (s) => s.items?.length > 0,
                  );
                if (!selectedDatasetFile && !hasCloudSelection) {
                  this.$router.push({
                    path: `/projects`,
                    query: {
                      pid: this.$route.query.pid,
                      stype: this.$route.query.stype,
                      ptab: 'mairaDatasets',
                    }
                  });
                } 
                else {
                  this.step = 2
                }

                return;
              }

              if(this.datasetType==='ts' && !this.selectedGptDataset.datasetFile){
                if (!res.data.detail?.task_id) {
                  this.$router.push({
                    path: `/projects`,
                    query: {
                      pid: this.$route.query.pid,
                      stype: this.$route.query.stype,
                      ptab: 'mairaDatasets',
                    }
                  });
                }
              }

              if (!['dictionary','ts'].includes(this.datasetType)) {
                this.step = step + 1;
                this.datasetUpdateBtnLoading= false;
              }

            }

          })
          .catch((err)=>{
            this.datasetUpdateBtnLoading=false;
              // url reachablity checking
              if(this.datasetType === 'url' && this.contentSource === 'webUrl'){
                this.checkUrlReachability(err);
                this.checkUrlDuplicacy(err,this.selectedGptDataset?.urls);
              }else if(this.datasetType === 'ts' && this.checkDatasetTsTimezoneErrors(err)){
                // timezone / datetime column errors are shown inline on the form
              }else{
              // Cloud files are not pre-validated in the browser, so the server's text is the only explanation of what was wrong —
              // same condition used above to decide whether cloudSources was even sent.
              const isCloud =
                this.selectedGptDataset.uploadSource === "cloud" &&
                this.selectedGptDataset.cloudSourcesDirty;
              const errMsg = this.getDatasetErrorMessage(err, isCloud)
              Toast.error(errMsg)
            }
          })
          .finally(()=>{
            this.datasetUpdateLoader = false;
            if(this.datasetType === 'url' && this.contentSource === 'webUrl'){
              this.isReachabiltyChecking = false
            }
          })
          
        },100)
      }
      if (step === 3) this.step = 1;
    },
    handleTraining() {
      const valid = this.validateForm();
      if (!valid) return;
      if (this.isDictionaryDataset) return;
      this.trainingBtnLoading = true;
      this.TRAIN_GPT_DATASET({
        project_id: this.$route.query.pid,
        datasetId: this.selectedGptDataset.datasetId,
        trainType: this.trainType,
        batchSize: this.batchSize,
      }).then(
        res => {
          this.gptSetupState = {
            ...this.gptSetupState,
            training: {
              ...this.gptSetupState.training,
              task_id: res.data?.detail?.task_id,
            },
          };
          this.trainingInProgress = true;
        },
        err => {
          console.error("train err", err.response);
          this.trainingInProgress = false;
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message:
                err.response?.data?.detail?.response ||
                "Dataset training error",
              color: "error",
            },
            { root: true },
          );
        },
        
      )
      .finally(() => {
          this.trainingBtnLoading = false;
        });
    },
    validateForm() {
      return this.$refs.train_form.validate();
    }
  },
};
</script>

<style></style>